import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';

import { Button } from 'antd';
import { CopyFilled, DeleteFilled, EditOutlined, PlusOutlined } from '@ant-design/icons';
import Page from 'components/Page';
import Table from 'components/Table';

import globalMessages from 'translations/messages';
import StatusTag from 'components/StatusTag';
import EditLicenseDrawer from './EditLicenseDrawer';

const QUERY = gql`
  query GetLicenses {
    licenses {
      id
      title
      uniqueTitle
      activeDataSources
      status
      level
    }
  }
`;

const DUPLICATE_LICENSE = gql`
  mutation DuplicateLicense($id: ID!) {
    duplicateLicense(id: $id) {
      id
    }
  }
`;

const DELETE_LICENSE = gql`
  mutation DeleteLicense($id: ID!) {
    deleteLicense(id: $id) {
      id
    }
  }
`;

const columns = [
  {
    dataIndex: 'uniqueTitle',
    title: 'Internal title',
    render: (uniqueTitle, record) => uniqueTitle || record.title,
    defaultSortOrder: 'ascend',
  },
  {
    dataIndex: 'title',
    title: 'Display title',
  },
  {
    dataIndex: 'status',
    title: 'Status',
    render: (status) => <StatusTag status={status} />,
    align: 'center',
  },
  {
    dataIndex: 'level',
    title: 'Level',
    type: 'number',
  },
  {
    dataIndex: 'activeDataSources',
    title: <FormattedMessage {...globalMessages.dataSources} />,
    type: 'number',
  },
];

const Licenses = () => {
  const { data, loading, refetch } = useQuery(QUERY);
  const [editLicense, setEditLicense] = useState(false);
  const [duplicateLicense] = useMutation(DUPLICATE_LICENSE, { onCompleted: refetch });
  const [deleteLicense] = useMutation(DELETE_LICENSE, { onCompleted: refetch });

  const dataSource = useMemo(() => data?.licenses.map((license) => ({
    ...license,
    uniqueTitle: license.uniqueTitle || license.title,
  })), [data]);

  const actions = (record) => (
    <Button type="primary" icon={<EditOutlined />} onClick={() => setEditLicense(record)}>
      Edit
    </Button>
  );

  const actionsMenuItems = (record) => [
    {
      key: 'duplicate',
      icon: <CopyFilled />,
      label: 'Duplicate',
      onClick: () => duplicateLicense({ variables: { id: record.id } }),
    },
    {
      key: 'delete',
      icon: <DeleteFilled />,
      label: 'Delete',
      danger: true,
      onClick: () => deleteLicense({ variables: { id: record.id } }),
      visible: record.activeDataSources === 0,
    },
  ];

  return (
    <Page>
      <Page.Header
        title="Legacy products"
      />
      <Page.Content>
        <Table
          loading={loading}
          columns={columns}
          dataSource={dataSource}
          actions={actions}
          actionsMenuItems={actionsMenuItems}
          footerActions={(
            <Button type="primary" onClick={() => setEditLicense({})} icon={<PlusOutlined />}>
              <FormattedMessage {...globalMessages.create} />
            </Button>
          )}
        />
        <EditLicenseDrawer license={editLicense} onDone={() => setEditLicense(false)} onCreate={refetch} />
      </Page.Content>
    </Page>
  );
};

export default Licenses;
